import { useState, useCallback, useRef } from 'react';
import * as orderService from '@/supabase/order/service/orderService';
import {
  DashboardSummary, DashboardSummaryDays, DASHBOARD_SUMMARY_DAYS_OPTIONS, DASHBOARD_SUMMARY_DAYS_DEFAULT,
} from '@/supabase/order/types/dashboardSummary';
import { useAuth } from './useAuth';
import { logError } from '@/lib/sentry';

// 의도적으로 디바이스 전역(계정 무관) 뷰 설정 — 저위험 표시 선호값이라 user.id 스코핑의
// 비동기 초기화 복잡도(user 도착 후 재읽기→이중 fetch)를 지지 않는다 (codexloop R1 #5 합의)
const SUMMARY_DAYS_STORAGE_KEY = 'dashboard-summary-days';

function readStoredSummaryDays(): DashboardSummaryDays {
  try {
    const raw = localStorage.getItem(SUMMARY_DAYS_STORAGE_KEY);
    const parsed = Number(raw);
    const match = DASHBOARD_SUMMARY_DAYS_OPTIONS.find((d) => d === parsed);
    return match ?? DASHBOARD_SUMMARY_DAYS_DEFAULT;
  } catch {
    return DASHBOARD_SUMMARY_DAYS_DEFAULT;
  }
}

// 요약 결과를 소유 계정과 함께 저장 — 계정 전환 첫 페인트(effect 발화 전)에 이전 계정
// 데이터가 노출되지 않도록 렌더 시점에 userId 일치 여부로 동기 게이트한다 (codexloop R2 #1)
interface SummaryState {
  userId: string;
  days: DashboardSummaryDays;
  data: DashboardSummary;
}

export function useDashboardData() {
  const { user } = useAuth();
  const [summaryState, setSummaryState] = useState<SummaryState | null>(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [summaryErrorState, setSummaryErrorState] = useState<{ userId: string; error: Error } | null>(null);
  const [summaryDays, setSummaryDaysState] = useState<DashboardSummaryDays>(readStoredSummaryDays);
  // 토글 연타 시 늦게 도착한 이전 요청이 최신 결과를 덮지 않도록 하는 stale 가드
  const summarySeqRef = useRef(0);

  const uid = user?.id ?? null;
  const summary = summaryState && summaryState.userId === uid ? summaryState.data : null;
  // 실제로 로드 완료된 데이터의 기간 — 토글 직후 라벨(선택값)과 stale 데이터(이전 기간)의 불일치 표기 방지
  const summaryLoadedDays = summaryState && summaryState.userId === uid ? summaryState.days : null;
  const summaryError = summaryErrorState && summaryErrorState.userId === uid ? summaryErrorState.error : null;

  const setSummaryDays = useCallback((days: DashboardSummaryDays) => {
    setSummaryDaysState(days);
    try {
      localStorage.setItem(SUMMARY_DAYS_STORAGE_KEY, String(days));
    } catch {
      // localStorage 불가 환경(시크릿 등)에서도 세션 내 동작은 유지
    }
  }, []);

  const fetchSummaryData = useCallback(async () => {
    if (!user) return;

    const seq = ++summarySeqRef.current;
    setIsSummaryLoading(true);
    setSummaryErrorState(null);
    try {
      const data = await orderService.getDashboardSummary(summaryDays);
      if (seq !== summarySeqRef.current) return;
      setSummaryState({ userId: user.id, days: summaryDays, data });
    } catch (err) {
      if (seq !== summarySeqRef.current) return;
      logError('대시보드 요약 데이터 fetching 실패:', err);
      setSummaryErrorState({ userId: user.id, error: err as Error });
    } finally {
      if (seq === summarySeqRef.current) {
        setIsSummaryLoading(false);
      }
    }
  }, [user, summaryDays]);

  return {
    summary,
    isSummaryLoading,
    summaryError,
    summaryDays,
    summaryLoadedDays,
    setSummaryDays,
    fetchSummaryData,
  };
}